/**
 * DKG Simple Withdrawal Service
 *
 * Simple withdrawal flow matching the single node backend:
 * - User provides: deposit TX hash, withdrawal_requests (array of {chain, token, denomination})
 * - Backend handles everything else:
 *   - Finds the correct merkle root for each denomination
 *   - Computes deterministic key index (NEVER exposed to frontend)
 *   - Returns keyshares for client-side reconstruction
 *
 * PRIVACY: The key index is computed by the backend using getDeterministicIndex.
 * Frontend NEVER sees or knows which key index is used until after the request.
 *
 * SUPPORTS: Multiple withdrawal requests per deposit (e.g., deposit 2 USDC,
 * withdraw 1 USDC on Base and 1 USDC on Solana)
 *
 * SUPPORTS: Both EVM (MetaMask) and Solana (Phantom) wallets
 */

import { ethers } from 'ethers';
import {
    reconstructPrivateKey,
    reconstructSolanaKeyFromSecp256k1Shares,
    verifyKeyMatchesAddress,
    isSolanaAddress,
} from './keyReconstructionService';
import { getGasOverrides } from './gasUtils';
import { getChainType, tronAddressToEvmHex } from '../utils/chainDetect';

// DKG node URLs
const DKG_NODE_URLS = [
    process.env.REACT_APP_DKG_NODE_1 || 'http://localhost:8081',
    process.env.REACT_APP_DKG_NODE_2 || 'http://localhost:8082',
    process.env.REACT_APP_DKG_NODE_3 || 'http://localhost:8083',
    process.env.REACT_APP_DKG_NODE_4 || 'http://localhost:8084',
    process.env.REACT_APP_DKG_NODE_5 || 'http://localhost:8085',
];

const DEFAULT_THRESHOLD = 3;

// Treasury contract ABI (MultiTokenTreasury - 7 params including token address)
const TREASURY_ABI = [
    {
        "inputs": [
            { "internalType": "address", "name": "token", "type": "address" },
            { "internalType": "address payable", "name": "recipient", "type": "address" },
            { "internalType": "uint256", "name": "amount", "type": "uint256" },
            { "internalType": "uint256", "name": "merkleRootId", "type": "uint256" },
            { "internalType": "bytes", "name": "signature", "type": "bytes" },
            { "internalType": "bytes32[]", "name": "merkleProof", "type": "bytes32[]" },
            { "internalType": "uint256", "name": "keyIndex", "type": "uint256" }
        ],
        "name": "withdraw",
        "outputs": [],
        "stateMutability": "nonpayable",
        "type": "function"
    }
];

/**
 * Classify and deduplicate DKG node errors into user-friendly messages.
 * When all/most nodes return the same error, show it once with a clear explanation.
 */
function classifyDKGError(failedResults) {
    if (!failedResults || failedResults.length === 0) {
        return { code: 'UNKNOWN', userMessage: 'Unknown error occurred', technical: '' };
    }

    // Deduplicate errors
    const errorCounts = {};
    for (const f of failedResults) {
        const msg = f.error || 'Unknown error';
        errorCounts[msg] = (errorCounts[msg] || 0) + 1;
    }

    // Find the most common error
    const sortedErrors = Object.entries(errorCounts).sort((a, b) => b[1] - a[1]);
    const [primaryError, primaryCount] = sortedErrors[0];
    const totalFailed = failedResults.length;

    // Classify by known error patterns
    if (primaryError.includes('Insufficient deposit balance')) {
        const match = primaryError.match(/need ([\d.]+) but only ([\d.]+) remaining/);
        const needed = match ? match[1] : '?';
        const remaining = match ? match[2] : '0';
        return {
            code: 'INSUFFICIENT_BALANCE',
            userMessage: remaining === '0.000000' || remaining === '0'
                ? 'Your deposit has been fully claimed. No remaining balance for new keys.'
                : `Insufficient balance: you need ${needed} but only ${remaining} remains.`,
            technical: primaryError,
        };
    }

    if (primaryError.includes('Balance recording failed')) {
        return {
            code: 'BALANCE_RECORDING_FAILED',
            userMessage: 'Balance update failed across all nodes. This is usually a temporary issue — please try again.',
            technical: primaryError,
        };
    }

    if (primaryError.includes('nullifier already used') || primaryError.includes('Key already used')) {
        return {
            code: 'KEY_ALREADY_USED',
            userMessage: 'This withdrawal key has already been used on-chain. Each key can only be used once.',
            technical: primaryError,
        };
    }

    if (primaryError.includes('deposit not found') || primaryError.includes('no deposit')) {
        return {
            code: 'DEPOSIT_NOT_FOUND',
            userMessage: 'Deposit transaction not found or not yet confirmed. Please verify the transaction hash and wait for confirmations.',
            technical: primaryError,
        };
    }

    if (primaryError.includes('signature') || primaryError.includes('proof verification')) {
        return {
            code: 'SIGNATURE_INVALID',
            userMessage: 'Wallet signature verification failed. Please ensure you are using the same wallet that made the deposit.',
            technical: primaryError,
        };
    }

    if (primaryError.includes('no available keys') || primaryError.includes('no keys available')) {
        return {
            code: 'NO_KEYS_AVAILABLE',
            userMessage: 'No withdrawal keys are available for this denomination. The key pool may be exhausted — please contact support.',
            technical: primaryError,
        };
    }

    if (primaryError.includes('denomination') && primaryError.includes('not supported')) {
        return {
            code: 'INVALID_DENOMINATION',
            userMessage: 'The requested denomination is not available on this chain. Please select a valid denomination.',
            technical: primaryError,
        };
    }

    if (primaryError.includes('ECONNREFUSED') || primaryError.includes('fetch failed') || primaryError.includes('NetworkError')) {
        return {
            code: 'NODE_UNREACHABLE',
            userMessage: `${primaryCount} of ${totalFailed} nodes are unreachable. The network may be experiencing issues — please try again later.`,
            technical: primaryError,
        };
    }

    // Generic fallback — still deduplicate
    if (totalFailed > 1 && primaryCount === totalFailed) {
        return {
            code: 'NODE_ERROR',
            userMessage: primaryError,
            technical: `All ${totalFailed} nodes returned the same error`,
        };
    }

    // Multiple different errors
    const uniqueErrors = sortedErrors.map(([msg, count]) => `${msg} (${count}x)`).join('; ');
    return {
        code: 'MIXED_ERRORS',
        userMessage: sortedErrors[0][0],
        technical: uniqueErrors,
    };
}

/**
 * Helper function to recursively sort object keys (matching Python's sort_keys=True)
 */
function sortKeysRecursive(obj) {
    if (Array.isArray(obj)) {
        return obj.map(item => sortKeysRecursive(item));
    } else if (obj !== null && typeof obj === 'object') {
        const sortedKeys = Object.keys(obj).sort();
        const sortedObj = {};
        sortedKeys.forEach(key => {
            sortedObj[key] = sortKeysRecursive(obj[key]);
        });
        return sortedObj;
    }
    return obj;
}

/**
 * Create the proof message that matches backend format
 * Must match: json.dumps(message_data, sort_keys=True) with Python's default separators
 */
function createProofMessage(depositTxHash, sourceChain, withdrawalRequests, userAddress, timestamp) {
    const message = {
        action: 'request_withdrawal',
        deposit_tx: depositTxHash,
        source_chain: sourceChain,
        timestamp: timestamp,
        user_address: userAddress,
        withdrawal_requests: withdrawalRequests.map(wr => ({
            denomination: wr.denomination,
            target_chain: wr.target_chain,
            token_symbol: wr.token_symbol,
        })),
    };

    // Sort keys recursively
    const sortedMessage = sortKeysRecursive(message);

    // Python's json.dumps() uses separators=(', ', ': ') by default (with spaces)
    const compactStr = JSON.stringify(sortedMessage);
    const messageStr = compactStr.replace(/,/g, ', ').replace(/:/g, ': ');

    return messageStr;
}

/**
 * Get available denominations for a chain
 * Returns list of { denomination, merkleRootId, tokenSymbol, chainName }
 * NOTE: Does NOT return key indices - that's private information
 */
async function getAvailableDenominations(chainName) {
    for (const nodeUrl of DKG_NODE_URLS) {
        try {
            const response = await fetch(`${nodeUrl}/api/config/merkle-roots?chain=${chainName}`);
            if (!response.ok) continue;

            const data = await response.json();
            if (data.merkle_roots) {
                return (data.merkle_roots || [])
                    .filter(r => (r.MerkleRootIDOnChain ?? r.merkle_root_id_on_chain ?? -1) >= 0)
                    .map(r => ({
                        denomination: r.Denomination || r.denomination,
                        merkleRootId: r.MerkleRootIDOnChain ?? r.merkle_root_id_on_chain,
                        tokenSymbol: r.TokenSymbol || r.token_symbol || 'ETH',
                        chainName: r.ChainName || r.chain_name,
                    }));
            }
        } catch (error) {
            console.warn(`Error fetching from ${nodeUrl}:`, error.message);
        }
    }
    return [];
}

/**
 * Request keyshares from a single DKG node
 * Uses the secure format - backend computes key index, not frontend
 */
async function requestKeyshareFromNode(nodeUrl, params) {
    try {
        const response = await fetch(`${nodeUrl}/api/v2/request-keyshare`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                deposit_tx_hash: params.depositTxHash,
                source_chain: params.sourceChain,
                withdrawal_requests: params.withdrawalRequests,
                user_address: params.userAddress,
                signature: params.signature,
                timestamp: params.timestamp,
                occurrence_offset: params.occurrenceOffset || 0,
                spend_request_id: params.spendRequestId || '',
            }),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Node returned ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        if (!data.success) {
            throw new Error(data.error || 'Unknown error from node');
        }

        return {
            success: true,
            nodeUrl,
            nodeId: data.node_id,
            keyshares: data.keyshares || [],  // Array of keyshare results (one per withdrawal request)
            threshold: data.threshold,
            totalNodes: data.total_nodes,
            depositAmount: data.deposit_amount,
            claimedAmount: data.claimed_amount,
            remainingDeposit: data.remaining_deposit,
        };
    } catch (error) {
        return {
            success: false,
            nodeUrl,
            error: error.message,
        };
    }
}

/**
 * DKG withdrawal with multiple withdrawal requests support
 * Matches single node backend functionality
 *
 * @param {Object} params
 * @param {string} params.depositTxHash - Deposit transaction hash
 * @param {string} params.sourceChain - Chain where deposit was made
 * @param {Array} params.withdrawalRequests - Array of {target_chain, token_symbol, denomination}
 * @param {string} params.recipient - Recipient address for withdrawal
 * @param {ethers.Signer} params.signer - User's signer for proof and tx
 * @param {ethers.Provider} params.provider - Ethereum provider
 * @param {string} params.treasuryAddress - Treasury contract address
 * @param {Function} onProgress - Progress callback
 */
async function executeSimpleWithdrawal(params, onProgress) {
    const {
        depositTxHash,
        sourceChain,
        withdrawalRequests,
        recipient,
        signer,
        provider,
        treasuryAddress,
    } = params;

    try {
        onProgress?.({ step: 'signing', message: 'Please sign the proof message...' });

        const userAddress = await signer.getAddress();
        const timestamp = Math.floor(Date.now() / 1000);

        const proofMessage = createProofMessage(
            depositTxHash,
            sourceChain,
            withdrawalRequests,
            userAddress,
            timestamp
        );

        console.log('Proof message to sign:', proofMessage);
        const signature = await signer.signMessage(proofMessage);

        // Request keyshares from ALL 5 nodes
        onProgress?.({ step: 'requesting', message: 'Requesting keyshares from DKG nodes...' });

        const occurrenceOffset = params.occurrenceOffset || 0;

        // Generate a unique spend_request_id for UTXO-based balance tracking.
        // Same ID goes to all 5 nodes so they can coordinate the atomic spend.
        const spendRequestId = ethers.utils.id(
            `${depositTxHash}:${sourceChain}:${occurrenceOffset}:${timestamp}`
        );

        const promises = DKG_NODE_URLS.map(nodeUrl =>
            requestKeyshareFromNode(nodeUrl, {
                depositTxHash,
                sourceChain,
                withdrawalRequests,
                userAddress,
                signature,
                timestamp,
                occurrenceOffset,
                spendRequestId,
            })
        );

        const results = await Promise.all(promises);
        const successful = results.filter(r => r.success && r.keyshares && r.keyshares.length > 0);
        const failed = results.filter(r => !r.success);

        console.log(`Keyshare results: ${successful.length}/${results.length} successful`);

        if (successful.length < DEFAULT_THRESHOLD) {
            const classified = classifyDKGError(failed);
            const err = new Error(classified.userMessage);
            err.code = classified.code;
            err.technical = classified.technical;
            err.nodeErrors = failed.map(f => f.error);
            throw err;
        }

        onProgress?.({ step: 'collected', message: `Collected ${successful.length}/5 keyshares` });

        // Process each withdrawal request
        const withdrawalResults = [];
        const network = await provider.getNetwork();
        const chainId = network.chainId;

        for (let wrIdx = 0; wrIdx < withdrawalRequests.length; wrIdx++) {
            const wr = withdrawalRequests[wrIdx];
            onProgress?.({ step: 'processing', message: `Processing withdrawal ${wrIdx + 1}/${withdrawalRequests.length}: ${wr.denomination} ${wr.token_symbol}` });

            // Extract keyshare data for this withdrawal request from each node
            const keyshareData = successful.map(r => {
                const ks = r.keyshares[wrIdx];
                return {
                    nodeId: r.nodeId,
                    shareId: ks.share_id,
                    shareValue: ks.share_value,
                    keyIndex: ks.key_index,
                    address: ks.address,
                    merkleRoot: ks.merkle_root,
                    merkleRootId: ks.merkle_root_id,
                    merkleProof: ks.merkle_proof || [],
                    denomination: ks.denomination,
                    chainName: ks.chain_name,
                    tokenSymbol: ks.token_symbol,
                    tokenAddress: ks.token_address || '0x0000000000000000000000000000000000000000',
                    tokenDecimals: ks.token_decimals || 18,
                };
            }).filter(ks => ks.shareId); // Filter out any undefined

            if (keyshareData.length < DEFAULT_THRESHOLD) {
                throw new Error(`Insufficient keyshares for withdrawal ${wrIdx + 1}`);
            }

            // Verify all nodes returned same address (security check)
            const expectedAddress = keyshareData[0].address;
            const expectedKeyIndex = keyshareData[0].keyIndex;
            for (const ks of keyshareData) {
                if (ks.address !== expectedAddress) {
                    throw new Error(`Security violation: nodes returned different addresses for withdrawal ${wrIdx + 1}`);
                }
                if (ks.keyIndex !== expectedKeyIndex) {
                    throw new Error(`Security violation: nodes returned different key indices for withdrawal ${wrIdx + 1}`);
                }
            }

            // Reconstruct private key
            onProgress?.({ step: 'reconstructing', message: `Reconstructing key ${wrIdx + 1}/${withdrawalRequests.length}...` });

            const { privateKeyHex } = reconstructPrivateKey(keyshareData);

            if (!verifyKeyMatchesAddress(privateKeyHex, expectedAddress)) {
                throw new Error(`Key reconstruction failed for withdrawal ${wrIdx + 1}: address mismatch`);
            }

            console.log(`Key ${wrIdx + 1} reconstructed, address:`, expectedAddress);

            // Sign withdrawal message
            onProgress?.({ step: 'signing_tx', message: `Signing withdrawal ${wrIdx + 1}/${withdrawalRequests.length}...` });

            const tokenDecimals = keyshareData[0].tokenDecimals || 18;
            const denominationWei = ethers.utils.parseUnits(wr.denomination, tokenDecimals);
            const tokenAddress = keyshareData[0].tokenAddress;
            const merkleRootId = keyshareData[0].merkleRootId;

            // Create message hash matching contract format: abi.encode(recipient, token, amount, merkleRootId, keyIndex, chainId)
            const encoded = ethers.utils.defaultAbiCoder.encode(
                ['address', 'address', 'uint256', 'uint256', 'uint256', 'uint256'],
                [recipient, tokenAddress, denominationWei, merkleRootId, expectedKeyIndex, chainId]
            );
            const messageHash = ethers.utils.keccak256(encoded);

            // Sign using wallet.signMessage() which adds Ethereum prefix (matches contract expectation)
            const wallet = new ethers.Wallet(privateKeyHex);
            const withdrawalSignature = await wallet.signMessage(ethers.utils.arrayify(messageHash));

            console.log(`Withdrawal ${wrIdx + 1} signature created for address:`, wallet.address);

            // Submit to contract
            onProgress?.({ step: 'submitting', message: `Submitting withdrawal ${wrIdx + 1}/${withdrawalRequests.length}...` });

            const contract = new ethers.Contract(treasuryAddress, TREASURY_ABI, signer);

            const merkleProof = (keyshareData[0].merkleProof || []).map(p =>
                p.startsWith('0x') ? p : '0x' + p
            );

            const gasOverrides = await getGasOverrides(provider);
            const tx = await contract.withdraw(
                tokenAddress,
                recipient,
                denominationWei,
                merkleRootId,
                withdrawalSignature,
                merkleProof,
                expectedKeyIndex,
                gasOverrides
            );

            onProgress?.({ step: 'pending', message: `Transaction ${wrIdx + 1} pending...`, txHash: tx.hash });

            const receipt = await tx.wait();

            withdrawalResults.push({
                success: true,
                txHash: receipt.transactionHash || receipt.hash,
                keyIndex: expectedKeyIndex,
                address: expectedAddress,
                denomination: wr.denomination,
                targetChain: wr.target_chain,
                tokenSymbol: wr.token_symbol,
            });

            console.log(`Withdrawal ${wrIdx + 1} complete:`, receipt.transactionHash || receipt.hash);
        }

        onProgress?.({ step: 'complete', message: `All ${withdrawalResults.length} withdrawal(s) successful!` });

        return {
            success: true,
            results: withdrawalResults,
            totalWithdrawals: withdrawalResults.length,
        };

    } catch (error) {
        console.error('DKG withdrawal failed:', error);
        onProgress?.({ step: 'error', message: error.message });
        return {
            success: false,
            error: error.message,
            errorCode: error.code || 'UNKNOWN',
            errorTechnical: error.technical || error.message,
        };
    }
}

/**
 * Request keys from DKG nodes and reconstruct them
 * Returns keys in the same format as single node backend for use with existing withdrawal code
 *
 * @param {Object} params
 * @param {string} params.depositTxHash - Deposit transaction hash
 * @param {string} params.sourceChain - Chain where deposit was made (e.g., 'sepolia')
 * @param {Array} params.withdrawalRequests - Array of {target_chain, token_symbol, denomination}
 * @param {ethers.Signer} params.signer - User's signer for proof
 * @param {Function} params.onProgress - Optional progress callback
 * @returns {Object} Keys in single-node format ready for executeWithdrawalWithKey()
 */
async function requestKeysOnly(params, onProgress) {
    const {
        depositTxHash,
        sourceChain,
        withdrawalRequests,
        signer,          // EVM signer (MetaMask) — used ONLY when isSolana=false and isTron=false
        solanaWallet,    // Solana wallet object (Phantom) — used ONLY when isSolana=true
        tronWallet,      // Tron wallet from useTronWallet — used ONLY when isTron=true
        isSolana,        // Boolean flag for Solana chains
        isTron,          // Boolean flag for Tron chains
        occurrenceOffset = 0, // Number of keys already claimed for this deposit
    } = params;

    try {
        onProgress?.({ step: 'signing', message: 'Please sign the proof message...' });

        let userAddress;
        let signature;
        const timestamp = Math.floor(Date.now() / 1000);

        // CHAIN-ISOLATION: route to the correct wallet for the SOURCE chain.
        // Never fall back to "whichever wallet is connected" — that would leak
        // the user's identity from one chain into another.
        if (isSolana && solanaWallet) {
            // Solana wallet signing
            userAddress = solanaWallet.publicKey.toString();

            const proofMessage = createProofMessage(
                depositTxHash,
                sourceChain,
                withdrawalRequests,
                userAddress,
                timestamp
            );

            console.log('Proof message to sign (Solana):', proofMessage);

            // Encode message for Solana signing
            const messageBytes = new TextEncoder().encode(proofMessage);

            // Sign with Solana wallet (Phantom)
            const signedMessage = await solanaWallet.signMessage(messageBytes);

            // Convert signature to hex string for backend
            signature = '0x' + Buffer.from(signedMessage).toString('hex');
            console.log('Solana signature:', signature);
        } else if (isTron && tronWallet?.tronWeb && tronWallet?.address) {
            // Tron wallet signing (TronLink). signMessageV2 is Ethereum-personal-sign
            // compatible — the backend's recoverSignerAddress + Tron T...→0x conversion
            // (in pkg/api/v2_routes.go) accepts this format directly.
            userAddress = tronWallet.address;

            const proofMessage = createProofMessage(
                depositTxHash,
                sourceChain,
                withdrawalRequests,
                userAddress,
                timestamp
            );

            console.log('Proof message to sign (Tron):', proofMessage);
            signature = await tronWallet.signMessage(proofMessage);
            console.log('Tron signature:', signature);
        } else if (signer && !isSolana && !isTron) {
            // EVM wallet signing (MetaMask)
            userAddress = await signer.getAddress();

            const proofMessage = createProofMessage(
                depositTxHash,
                sourceChain,
                withdrawalRequests,
                userAddress,
                timestamp
            );

            console.log('Proof message to sign (EVM):', proofMessage);
            signature = await signer.signMessage(proofMessage);
        } else {
            throw new Error(`No appropriate wallet for source chain ${sourceChain} (isSolana=${isSolana}, isTron=${isTron})`);
        }

        // Request from all DKG nodes
        onProgress?.({ step: 'requesting', message: 'Requesting keyshares from DKG nodes...' });

        // Generate a unique spend_request_id for UTXO-based balance tracking.
        // Same ID goes to all 5 nodes so they can coordinate the atomic spend.
        const spendRequestId = ethers.utils.id(
            `${depositTxHash}:${sourceChain}:${occurrenceOffset}:${timestamp}`
        );

        const promises = DKG_NODE_URLS.map(nodeUrl =>
            requestKeyshareFromNode(nodeUrl, {
                depositTxHash,
                sourceChain,
                withdrawalRequests,
                userAddress,
                signature,
                timestamp,
                occurrenceOffset,
                spendRequestId,
            })
        );

        const results = await Promise.all(promises);
        const successful = results.filter(r => r.success && r.keyshares && r.keyshares.length > 0);
        const failed = results.filter(r => !r.success);

        console.log(`Keyshare results: ${successful.length}/${results.length} successful`);

        if (successful.length < DEFAULT_THRESHOLD) {
            const classified = classifyDKGError(failed);
            const err = new Error(classified.userMessage);
            err.code = classified.code;
            err.technical = classified.technical;
            err.nodeErrors = failed.map(f => f.error);
            throw err;
        }

        onProgress?.({ step: 'collected', message: `Collected ${successful.length}/5 keyshares` });

        // Reconstruct keys for each withdrawal request
        const keys = [];

        for (let wrIdx = 0; wrIdx < withdrawalRequests.length; wrIdx++) {
            const wr = withdrawalRequests[wrIdx];
            onProgress?.({ step: 'reconstructing', message: `Reconstructing key ${wrIdx + 1}/${withdrawalRequests.length}...` });

            // Extract keyshare data for this withdrawal request from each node
            const keyshareData = successful.map(r => {
                const ks = r.keyshares[wrIdx];
                return {
                    nodeId: r.nodeId,
                    shareId: ks.share_id,
                    shareValue: ks.share_value,
                    keyIndex: ks.key_index,
                    address: ks.address,
                    merkleRoot: ks.merkle_root,
                    merkleRootId: ks.merkle_root_id,
                    merkleProof: ks.merkle_proof || [],
                    denomination: ks.denomination,
                    chainName: ks.chain_name,
                    chainId: ks.chain_id, // From backend config
                    treasuryAddress: ks.treasury_address, // From backend config
                    tokenSymbol: ks.token_symbol,
                    tokenAddress: ks.token_address, // ERC20 address from backend config
                    tokenDecimals: ks.token_decimals || 18, // Token decimals (6 for USDC, 18 for ETH)
                    treeIndex: ks.tree_index,
                };
            }).filter(ks => ks.shareId);

            if (keyshareData.length < DEFAULT_THRESHOLD) {
                throw new Error(`Insufficient keyshares for withdrawal ${wrIdx + 1}`);
            }

            // Verify all nodes returned same address (security check)
            const expectedAddress = keyshareData[0].address;
            const expectedKeyIndex = keyshareData[0].keyIndex;
            for (const ks of keyshareData) {
                if (ks.address !== expectedAddress) {
                    throw new Error(`Security violation: nodes returned different addresses for withdrawal ${wrIdx + 1}`);
                }
                if (ks.keyIndex !== expectedKeyIndex) {
                    throw new Error(`Security violation: nodes returned different key indices for withdrawal ${wrIdx + 1}`);
                }
            }

            // Detect if this is a Solana address (base58) or EVM address (hex)
            const isSolana = isSolanaAddress(expectedAddress);
            let privateKeyHex;

            if (isSolana) {
                // Reconstruct Solana key from secp256k1 shares
                // Backend returns secp256k1 shares; we convert to Ed25519 on client
                const result = await reconstructSolanaKeyFromSecp256k1Shares(keyshareData, expectedAddress);
                privateKeyHex = result.privateKeyHex;
                console.log(`Solana key ${wrIdx + 1} reconstructed (secp256k1→Ed25519), address:`, expectedAddress);
            } else {
                // Reconstruct secp256k1 private key for EVM
                // Try default first-3 shares, then fallback to all 3-of-N combinations
                // if a node returned a corrupted share
                const result = reconstructPrivateKey(keyshareData);
                privateKeyHex = result.privateKeyHex;

                if (!verifyKeyMatchesAddress(privateKeyHex, expectedAddress)) {
                    console.warn(`Default shares [${keyshareData.slice(0, 3).map(s => s.nodeId).join(',')}] failed address check, trying alternative combinations...`);
                    let found = false;

                    // Generate all C(N,3) combinations
                    for (let a = 0; a < keyshareData.length && !found; a++) {
                        for (let b = a + 1; b < keyshareData.length && !found; b++) {
                            for (let c = b + 1; c < keyshareData.length && !found; c++) {
                                // Skip the default [0,1,2] combination we already tried
                                if (a === 0 && b === 1 && c === 2) continue;
                                const subset = [keyshareData[a], keyshareData[b], keyshareData[c]];
                                try {
                                    const altResult = reconstructPrivateKey(subset);
                                    if (verifyKeyMatchesAddress(altResult.privateKeyHex, expectedAddress)) {
                                        privateKeyHex = altResult.privateKeyHex;
                                        found = true;
                                        console.log(`Reconstruction succeeded with shares from nodes [${subset.map(s => s.nodeId).join(',')}]`);
                                    }
                                } catch (e) {
                                    // Skip invalid combination
                                }
                            }
                        }
                    }

                    if (!found) {
                        throw new Error(`Key reconstruction failed for withdrawal ${wrIdx + 1}: no valid 3-of-${keyshareData.length} share combination produces the expected address`);
                    }
                }
                console.log(`ECDSA key ${wrIdx + 1} reconstructed for EVM, address:`, expectedAddress);
            }

            // Convert denomination to smallest unit using token decimals
            // ETH = 18 decimals, USDC = 6 decimals, etc.
            const tokenDecimals = keyshareData[0].tokenDecimals || 18;
            const denominationWei = ethers.utils.parseUnits(wr.denomination, tokenDecimals).toString();

            // Format merkle proof with 0x prefix
            const merkleProof = (keyshareData[0].merkleProof || []).map(p =>
                p.startsWith('0x') ? p : '0x' + p
            );

            // Format merkle root with 0x prefix
            const merkleRoot = keyshareData[0].merkleRoot.startsWith('0x')
                ? keyshareData[0].merkleRoot
                : '0x' + keyshareData[0].merkleRoot;

            // Return key in same format as single node backend
            // All config (chain_id, treasury_address, token_address) comes from backend
            keys.push({
                private_key: privateKeyHex,
                ethereum_address: expectedAddress,
                solana_address: isSolana ? expectedAddress : undefined,
                denomination_wei: denominationWei,
                merkle_root: merkleRoot,
                merkle_proof: merkleProof,
                key_index: expectedKeyIndex,
                tree_index: keyshareData[0].treeIndex || expectedKeyIndex,
                token_address: keyshareData[0].tokenAddress || '0x0000000000000000000000000000000000000000',
                token_symbol: wr.token_symbol || 'ETH',
                merkle_root_id: keyshareData[0].merkleRootId,
                // Chain info from backend config
                chain_name: keyshareData[0].chainName || wr.target_chain,
                chain_id: keyshareData[0].chainId,
                treasury_address: keyshareData[0].treasuryAddress,
                // Extra info
                denomination: wr.denomination,
            });
        }

        onProgress?.({ step: 'complete', message: `Successfully reconstructed ${keys.length} key(s)` });

        // Get balance info using the MAX claimedAmount across all successful nodes.
        // Each node tracks claims in its own local DB, so a node that missed a previous
        // request may report a stale (lower) claimedAmount. Taking the max ensures accuracy.
        const depositAmount = successful[0]?.depositAmount || 0;
        const maxClaimedAmount = Math.max(...successful.map(s => s.claimedAmount || 0));
        const remainingDeposit = depositAmount - maxClaimedAmount;

        return {
            success: true,
            keys: keys,
            total_value: keys.reduce((sum, k) => sum + parseFloat(k.denomination), 0).toString(),
            depositTxHash,
            sourceChain,
            depositAmount,
            claimedAmount: maxClaimedAmount,
            remainingDeposit: Math.max(0, remainingDeposit),
        };

    } catch (error) {
        console.error('DKG key request failed:', error);
        onProgress?.({ step: 'error', message: error.message });
        return {
            success: false,
            error: error.message,
            errorCode: error.code || 'UNKNOWN',
            errorTechnical: error.technical || error.message,
        };
    }
}

/**
 * Execute a gas-free relayed withdrawal using reconstructed keys.
 * Instead of submitting a tx to the contract, this signs the relayer message
 * and submits to the DKG node relayer endpoint.
 *
 * @param {Object} params
 * @param {Object} params.key - Reconstructed key from requestKeysOnly()
 * @param {string} params.recipientAddress - Recipient address
 * @param {Object} params.relayInfo - From relayService.getRelayInfo()
 * @param {string} params.maxRelayerFee - Max fee in wei
 * @param {string} params.nodeUrl - DKG node URL
 * @param {boolean} params.isSwap - Whether this is a swap withdrawal
 * @param {string} params.targetAddress - Swap target (NEAR Intents deposit address)
 * @param {Function} onProgress - Progress callback
 * @returns {Object} Relay job result
 */
async function executeRelayedWithdrawal(params, onProgress) {
    const {
        key,
        recipientAddress,
        relayInfo,
        maxRelayerFee,
        nodeUrl,
        isSwap,
        targetAddress,
    } = params;

    try {
        onProgress?.({ step: 'signing_relay', message: 'Signing relayed withdrawal message...' });

        const wallet = new ethers.Wallet(key.private_key);
        const chainId = key.chain_id;
        const tokenAddress = key.token_address || '0x0000000000000000000000000000000000000000';
        const amount = ethers.BigNumber.from(key.denomination_wei);

        // Detect target-chain type from the keyshare's address. The Solidity
        // contract on all three chains expects 20-byte EVM-style addresses in
        // the ABI-encoded signature payload — but Tron user-facing addresses
        // are Base58Check. Convert to EVM hex for the signature; keep T... in
        // the relay request body (the backend validates that format).
        const keyAddrType = getChainType(key.ethereum_address || key.solana_address);
        const isTron = keyAddrType === 'tron';

        // Relayer address: per-chain (the DKG node exposes evm_relayer_address,
        // solana_relayer_address, tron_relayer_address on /api/v2/relay/info).
        let relayerAddress;
        if (isTron) {
            relayerAddress = relayInfo.tron_relayer_address;
            if (!relayerAddress) throw new Error('Tron relayer not configured on the DKG node');
        } else {
            relayerAddress = relayInfo.evm_relayer_address;
        }

        // ABI-encoded recipient + relayer must be in 20-byte EVM hex form for
        // the signature recovery on chain to match. Convert from Tron base58 if needed.
        const recipientForSig = isTron && getChainType(recipientAddress) === 'tron'
            ? tronAddressToEvmHex(recipientAddress)
            : recipientAddress;
        const relayerForSig = isTron && getChainType(relayerAddress) === 'tron'
            ? tronAddressToEvmHex(relayerAddress)
            : relayerAddress;

        // Build the message hash matching the contract using abi.encode (not encodePacked)
        const merkleRootId = key.merkle_root_id;
        const keyIndex = key.key_index;

        let messageHash;
        if (isSwap && targetAddress) {
            // withdrawToAddress: abi.encode(recipient, targetAddress, token, amount, merkleRootId, keyIndex, chainId, relayer, maxRelayerFee)
            const encoded = ethers.utils.defaultAbiCoder.encode(
                ['address', 'address', 'address', 'uint256', 'uint256', 'uint256', 'uint256', 'address', 'uint256'],
                [recipientForSig, targetAddress, tokenAddress, amount, merkleRootId, keyIndex, chainId, relayerForSig, maxRelayerFee]
            );
            messageHash = ethers.utils.keccak256(encoded);
        } else {
            // withdrawViaRelayer: abi.encode(recipient, token, amount, merkleRootId, keyIndex, chainId, relayer, maxRelayerFee)
            const encoded = ethers.utils.defaultAbiCoder.encode(
                ['address', 'address', 'uint256', 'uint256', 'uint256', 'uint256', 'address', 'uint256'],
                [recipientForSig, tokenAddress, amount, merkleRootId, keyIndex, chainId, relayerForSig, maxRelayerFee]
            );
            messageHash = ethers.utils.keccak256(encoded);
        }

        // Sign with Ethereum prefix (matches contract ecrecover). Works for
        // both EVM and Tron because Tron's withdrawViaRelayer uses the same
        // Solidity ABI + ecrecover semantics.
        const signature = await wallet.signMessage(ethers.utils.arrayify(messageHash));

        onProgress?.({ step: 'submitting_relay', message: 'Submitting to relayer node...' });

        // Build relay request. The backend validates recipient against the
        // chain-specific address regex, so for Tron we send the T... form;
        // for EVM we send 0x...
        const relayRequest = {
            chain: key.chain_name,
            chain_type: isTron ? 'tron' : 'evm',
            token: tokenAddress,
            recipient: recipientAddress,
            amount: key.denomination_wei,
            merkle_root_id: key.merkle_root_id,
            signature: signature,
            merkle_proof: key.merkle_proof || [],
            key_index: key.key_index,
            max_relayer_fee: maxRelayerFee,
            is_swap: !!isSwap,
        };

        if (isSwap && targetAddress) {
            relayRequest.target_address = targetAddress;
        }

        const endpoint = isSwap
            ? `${nodeUrl}/api/v2/relay/withdraw-and-swap`
            : `${nodeUrl}/api/v2/relay/withdraw`;

        const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(relayRequest),
        });

        const data = await response.json();
        if (!data.success) {
            throw new Error(data.error || 'Relay submission failed');
        }

        const job = data.job;
        onProgress?.({ step: 'relay_submitted', message: `Relay submitted! Job: ${job.id}`, jobId: job.id });

        // Poll for completion
        onProgress?.({ step: 'relay_polling', message: 'Waiting for relay confirmation...' });

        const pollResult = await pollRelayJob(job.id, nodeUrl, (jobUpdate) => {
            onProgress?.({
                step: 'relay_polling',
                message: `Status: ${jobUpdate.status}`,
                txHash: jobUpdate.tx_hash,
            });
        });

        if (pollResult.success) {
            onProgress?.({ step: 'relay_complete', message: 'Relay confirmed!', txHash: pollResult.job.tx_hash });
            return {
                success: true,
                txHash: pollResult.job.tx_hash,
                gasUsed: pollResult.job.gas_used,
                jobId: job.id,
            };
        } else {
            throw new Error(pollResult.error || 'Relay failed');
        }

    } catch (error) {
        console.error('Relayed withdrawal failed:', error);
        onProgress?.({ step: 'error', message: error.message });
        return { success: false, error: error.message };
    }
}

/**
 * Poll a relay job until it completes or fails
 */
async function pollRelayJob(jobId, nodeUrl, onStatus, maxWaitMs = 300000) {
    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitMs) {
        try {
            const response = await fetch(`${nodeUrl}/api/v2/relay/status/${jobId}`);
            const data = await response.json();

            if (data.success && data.job) {
                onStatus?.(data.job);

                if (data.job.status === 'confirmed') {
                    return { success: true, job: data.job };
                }
                if (data.job.status === 'failed') {
                    return { success: false, error: data.job.error || 'Relay failed', job: data.job };
                }
            }
        } catch (err) {
            console.warn('Poll error:', err.message);
        }

        await new Promise(resolve => setTimeout(resolve, 3000));
    }

    return { success: false, error: 'Relay timed out' };
}

/**
 * Legacy function - now just calls requestKeysOnly
 * @deprecated Use requestKeysOnly instead
 */
async function requestKeysharesOnly(params) {
    return requestKeysOnly(params);
}

/**
 * Execute a relayed (gas-free) or swap withdrawal for Solana keys.
 *
 * The frontend signs the message with Ed25519, then the relayer node
 * builds the on-chain transaction (Ed25519 verify ix + withdraw ix),
 * signs as relayer, and submits to Solana.
 *
 * @param {Object} params
 * @param {Object} params.key - Solana key with private_key, denomination, merkle_root_id, etc.
 * @param {string} params.recipientAddress - Solana recipient (base58)
 * @param {Object} params.relayInfo - From getRelayInfo()
 * @param {string} params.maxRelayerFee - Max fee in lamports
 * @param {string} params.nodeUrl - DKG node URL
 * @param {boolean} params.isSwap - Whether this is a swap withdrawal
 * @param {string} params.targetAddress - Swap target Solana address
 * @param {Function} onProgress - Progress callback
 */
async function executeSolanaRelayedWithdrawal(params, onProgress) {
    const {
        key,
        recipientAddress,
        relayInfo,
        maxRelayerFee,
        nodeUrl,
        isSwap,
        targetAddress,
    } = params;

    try {
        onProgress?.({ step: 'signing_relay', message: 'Signing Solana relayed withdrawal...' });

        // Dynamic imports for Solana + Ed25519
        const { PublicKey, Keypair } = await import('@solana/web3.js');
        const nacl = await import('tweetnacl');
        const { keccak256 } = await import('js-sha3');

        // Parse private key
        const privateKeyHex = key.private_key.startsWith('0x')
            ? key.private_key.slice(2)
            : key.private_key;
        const privateKeyBytes = Buffer.from(privateKeyHex, 'hex');

        let keyPair;
        if (privateKeyBytes.length === 32) {
            keyPair = Keypair.fromSeed(privateKeyBytes);
        } else if (privateKeyBytes.length === 64) {
            keyPair = Keypair.fromSecretKey(privateKeyBytes);
        } else {
            throw new Error(`Invalid private key length: ${privateKeyBytes.length} bytes`);
        }

        const publicKeyBytes = keyPair.publicKey.toBytes();
        const publicKeyHex = '0x' + Buffer.from(publicKeyBytes).toString('hex');

        // Determine token and amount
        const tokenSymbol = key.token_symbol || 'SOL';
        const isNativeSOL = tokenSymbol === 'SOL' || !key.token_address ||
            key.token_address === '11111111111111111111111111111111';

        // Parse amount to base units
        let amountBaseUnits;
        if (key.denomination_wei) {
            amountBaseUnits = BigInt(key.denomination_wei);
        } else {
            const decimals = isNativeSOL ? 9 : 6;
            amountBaseUnits = BigInt(Math.floor(parseFloat(key.denomination) * Math.pow(10, decimals)));
        }

        // Token pubkey (all zeros for native SOL)
        let tokenPubkeyBytes;
        if (isNativeSOL) {
            tokenPubkeyBytes = Buffer.alloc(32); // Pubkey::default()
        } else {
            // Resolve SPL token mint
            let mintAddress = key.token_address;
            if (!mintAddress || mintAddress.startsWith('0x')) {
                // Lookup from supported tokens
                const solanaService = (await import('./solanaInternullService')).default;
                const tokens = await solanaService.getSupportedTokens();
                const tokenInfo = tokens.find(t => t.symbol === tokenSymbol);
                if (tokenInfo) mintAddress = tokenInfo.address;
                else throw new Error(`Cannot resolve Solana mint for ${tokenSymbol}`);
            }
            tokenPubkeyBytes = new PublicKey(mintAddress).toBuffer();
        }

        // Relayer pubkey
        const relayerAddress = relayInfo.solana_relayer_address;
        if (!relayerAddress) {
            throw new Error('Solana relayer address not available. The relayer may not support Solana.');
        }
        const relayerPubkeyBytes = new PublicKey(relayerAddress).toBuffer();

        const CHAIN_ID_SOLANA = 900n;
        const maxFee = BigInt(maxRelayerFee);

        // Build LE u64 buffers
        const amountBuf = Buffer.alloc(8);
        amountBuf.writeBigUInt64LE(amountBaseUnits);
        const chainIdBuf = Buffer.alloc(8);
        chainIdBuf.writeBigUInt64LE(CHAIN_ID_SOLANA);
        const maxFeeBuf = Buffer.alloc(8);
        maxFeeBuf.writeBigUInt64LE(maxFee);

        const recipientPubkeyBytes = new PublicKey(recipientAddress).toBuffer();

        // Create message hash matching the Solana contract
        let messageData;
        if (isSwap && targetAddress) {
            // Swap: keccak256(recipient || targetAddress || token || amount || chainId || relayer || maxRelayerFee)
            const targetBytes = new PublicKey(targetAddress).toBuffer();
            messageData = Buffer.concat([
                recipientPubkeyBytes, targetBytes, tokenPubkeyBytes,
                amountBuf, chainIdBuf, relayerPubkeyBytes, maxFeeBuf,
            ]);
        } else {
            // Relayer: keccak256(recipient || token || amount || chainId || relayer || maxRelayerFee)
            messageData = Buffer.concat([
                recipientPubkeyBytes, tokenPubkeyBytes,
                amountBuf, chainIdBuf, relayerPubkeyBytes, maxFeeBuf,
            ]);
        }

        const messageHash = Buffer.from(keccak256(messageData), 'hex');
        console.log('Relay message hash:', messageHash.toString('hex'));

        // Sign with Ed25519
        const signature = nacl.default.sign.detached(messageHash, keyPair.secretKey);
        const signatureHex = '0x' + Buffer.from(signature).toString('hex');
        console.log('Relay signature:', signatureHex.slice(0, 20) + '...');

        onProgress?.({ step: 'submitting_relay', message: 'Submitting to Solana relayer...' });

        // Build relay request
        const relayRequest = {
            chain: key.chain_name || 'solana-devnet',
            chain_type: 'solana',
            token: isNativeSOL ? '' : key.token_address,
            recipient: recipientAddress,
            amount: amountBaseUnits.toString(),
            merkle_root_id: key.merkle_root_id,
            signature: signatureHex,
            public_key: publicKeyHex,
            merkle_proof: (key.merkle_proof || []).map(p => p.startsWith('0x') ? p : '0x' + p),
            key_index: key.key_index,
            max_relayer_fee: maxFee.toString(),
            is_swap: !!isSwap,
        };

        // For SPL tokens, include treasury token account
        if (!isNativeSOL) {
            const { getAssociatedTokenAddress } = await import('@solana/spl-token');
            const treasuryPDA = PublicKey.findProgramAddressSync(
                [Buffer.from('treasury')],
                new PublicKey('5zjh95A1KgD6R7MidR6vDUCYAoKpmKcfNhMbRBkiLsmE')
            )[0];
            const tokenMint = new PublicKey(key.token_address);
            const treasuryTokenAccount = await getAssociatedTokenAddress(tokenMint, treasuryPDA, true);
            relayRequest.treasury_token_account = treasuryTokenAccount.toString();
        }

        if (isSwap && targetAddress) {
            relayRequest.target_address = targetAddress;
        }

        console.log('Solana relay request:', { ...relayRequest, signature: '...', public_key: '...' });

        const endpoint = isSwap
            ? `${nodeUrl}/api/v2/relay/withdraw-and-swap`
            : `${nodeUrl}/api/v2/relay/withdraw`;

        const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(relayRequest),
        });

        const data = await response.json();
        if (!data.success) {
            throw new Error(data.error || 'Solana relay submission failed');
        }

        const job = data.job;
        onProgress?.({ step: 'relay_submitted', message: `Relay submitted! Job: ${job.id}`, jobId: job.id });

        // Poll for completion
        onProgress?.({ step: 'relay_polling', message: 'Waiting for Solana relay confirmation...' });

        const pollResult = await pollRelayJob(job.id, nodeUrl, (jobUpdate) => {
            onProgress?.({
                step: 'relay_polling',
                message: `Status: ${jobUpdate.status}`,
                txHash: jobUpdate.tx_hash,
            });
        });

        if (pollResult.success) {
            onProgress?.({ step: 'relay_complete', message: 'Solana relay confirmed!', txHash: pollResult.job.tx_hash });
            return {
                success: true,
                txHash: pollResult.job.tx_hash,
                jobId: job.id,
            };
        } else {
            throw new Error(pollResult.error || 'Solana relay failed');
        }

    } catch (error) {
        console.error('Solana relayed withdrawal failed:', error);
        onProgress?.({ step: 'error', message: error.message });
        return { success: false, error: error.message };
    }
}

export {
    requestKeysOnly,
    requestKeysharesOnly, // deprecated alias
    executeSimpleWithdrawal,
    executeRelayedWithdrawal,
    executeSolanaRelayedWithdrawal,
    getAvailableDenominations,
};
